import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, CheckCircle2, ArrowRight, ShieldCheck, Star, Users, Phone, Mail, Lock } from "lucide-react";
import { toast } from "sonner";

export default function FunnelView() {
  const { slug } = useParams();
  const [step, setStep] = useState(1); // 1: Landing Page, 2: Checkout / Booking, 3: Thank You
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });

  const funnelName = (slug || "high-converting-funnel")
    .split("-")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

  const handleOptIn = (e) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error("Please enter your email");
      return;
    }
    setStep(2);
    toast.success("Step 1 Complete! Advancing to booking confirmation.");
  };

  const handleFinalize = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setStep(3);
    toast.success("Congratulations! Your discovery session has been secured.");
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col font-sans selection:bg-blue-600 selection:text-white">
      {/* Top Bar */}
      <header className="border-b border-slate-800/80 bg-slate-900/60 backdrop-blur-md px-6 py-3.5 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center font-bold text-white shadow-xs">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <span className="font-bold text-sm tracking-tight text-white">{funnelName}</span>
            <Badge variant="outline" className="ml-2 text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
              Live Published Funnel
            </Badge>
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-400">
          <span className="hidden sm:inline">Step {step} of 3</span>
          <div className="flex gap-1">
            <span className={`w-6 h-1.5 rounded-full ${step >= 1 ? "bg-blue-500" : "bg-slate-800"}`} />
            <span className={`w-6 h-1.5 rounded-full ${step >= 2 ? "bg-blue-500" : "bg-slate-800"}`} />
            <span className={`w-6 h-1.5 rounded-full ${step >= 3 ? "bg-emerald-500" : "bg-slate-800"}`} />
          </div>
          <Link to="/sites?tab=funnels" className="ml-2 text-slate-400 hover:text-white transition-colors underline">
            Back to Funnels
          </Link>
        </div>
      </header>

      {/* Main Container */}
      <main className="flex-1 flex items-center justify-center p-6 sm:p-12 relative overflow-hidden">
        {/* Glow backdrop */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-2xl w-full z-10 space-y-8">
          {step === 1 && (
            <div className="space-y-6 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span>Special Invitation · Limited Availability</span>
              </div>

              <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight leading-tight">
                Unlock High-Velocity Growth with {funnelName}
              </h1>

              <p className="text-sm sm:text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
                Transform your revenue with automated AI client acquisition, content production, and enterprise reputation management.
              </p>

              {/* Opt-In Form Card */}
              <Card className="p-6 sm:p-8 bg-slate-900/90 border-slate-800 text-left shadow-2xl rounded-2xl space-y-4">
                <h3 className="text-lg font-bold text-white">Claim Your Private Strategy Session</h3>
                <form onSubmit={handleOptIn} className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-300">Your Full Name</label>
                    <Input
                      required
                      placeholder="Alex Taylor"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white mt-1 h-11"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-300">Business Email *</label>
                    <Input
                      required
                      type="email"
                      placeholder="alex@company.com"
                      value={formData.email}
                      onChange={e => setFormData({ ...formData, email: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white mt-1 h-11"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-300">Direct Mobile Phone</label>
                    <Input
                      placeholder="(555) 000-1234"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-slate-950 border-slate-800 text-white mt-1 h-11"
                    />
                  </div>

                  <Button type="submit" className="w-full h-12 bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-lg gap-2 mt-2">
                    <span>Continue to Step 2</span>
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </form>
              </Card>

              {/* Social Proof */}
              <div className="flex items-center justify-center gap-6 pt-4 text-xs text-slate-400">
                <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-400" /> 256-Bit Encrypted</span>
                <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-amber-400 fill-amber-400" /> 4.9/5 Rating (120+ Reviews)</span>
              </div>
            </div>
          )}

          {step === 2 && (
            <Card className="p-6 sm:p-8 bg-slate-900 border-slate-800 text-left shadow-2xl rounded-2xl space-y-6">
              <div className="space-y-1">
                <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">Step 2: Confirm Reservation</Badge>
                <h2 className="text-2xl font-bold text-white">Almost Done, {formData.name || "Partner"}!</h2>
                <p className="text-xs text-slate-400">Confirm your session time and primary objective.</p>
              </div>

              <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 space-y-2 text-xs">
                <div className="flex justify-between text-slate-300">
                  <span>Selected Package:</span>
                  <span className="font-semibold text-white">{funnelName} Access</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Contact Email:</span>
                  <span className="font-semibold text-white">{formData.email}</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Onboarding Fee:</span>
                  <span className="font-bold text-emerald-400">Complimentary ($0.00)</span>
                </div>
              </div>

              <form onSubmit={handleFinalize} className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-slate-300">Preferred Meeting Date / Window</label>
                  <select className="w-full h-11 px-3 mt-1 rounded-lg bg-slate-950 border border-slate-800 text-white text-xs">
                    <option>Tomorrow at 10:00 AM (EST)</option>
                    <option>Tomorrow at 02:00 PM (EST)</option>
                    <option>Friday at 11:30 AM (EST)</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(1)} className="border-slate-700 text-slate-300">
                    &larr; Back
                  </Button>
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold h-11 gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Confirm &amp; Finalize Booking</span>
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {step === 3 && (
            <Card className="p-8 bg-slate-900 border-slate-800 text-center shadow-2xl rounded-2xl space-y-6">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto text-2xl shadow-lg border border-emerald-500/40">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div className="space-y-2">
                <h2 className="text-3xl font-extrabold text-white">You&apos;re Confirmed!</h2>
                <p className="text-sm text-slate-300 max-w-md mx-auto">
                  A calendar invite and preparation guide have been sent to <span className="text-white font-semibold">{formData.email}</span>.
                </p>
              </div>

              <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-400 space-y-1">
                <p>Funnel conversion tracked: <span className="text-emerald-400 font-semibold">+1 Lead</span></p>
                <p>Status: <span className="text-blue-400 font-semibold">Active in CRM Pipeline</span></p>
              </div>

              <div className="flex justify-center gap-3 pt-2">
                <Link to="/sites?tab=funnels">
                  <Button className="bg-blue-600 hover:bg-blue-500 text-white font-semibold">
                    Return to Funnels
                  </Button>
                </Link>
                <Link to="/contacts">
                  <Button variant="outline" className="border-slate-700 text-slate-300">
                    View in Contacts CRM
                  </Button>
                </Link>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}

